import React, { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Card } from "@/components/ui/card";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Palette, Check, Save, Trash2, Upload, X } from "lucide-react";
import { useTheme, PRESETS, HEADING_FONTS, RADII } from "@/context/ThemeContext";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

export default function ThemeSettings() {
  const { user } = useAuth();
  const { theme, setPreset, setCustom, setHeadingFont, setRadius, saved, saveCurrent, applySaved, removeSaved, publishTeamTheme } = useTheme();
  const [open, setOpen] = useState(false);
  const [saveName, setSaveName] = useState("");
  const grad = (p) => `linear-gradient(135deg, ${p.from} 0%, ${p.via} 55%, ${p.to} 100%)`;

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="ghost" size="icon" data-testid="theme-btn" className="hover:bg-slate-100"><Palette className="w-4 h-4" /></Button>
      </DialogTrigger>
      <DialogContent className="max-w-xl max-h-[85vh] overflow-y-auto" data-testid="theme-dialog">
        <DialogHeader><DialogTitle className="flex items-center gap-2"><Palette className="w-5 h-5" /> Customize your look</DialogTitle></DialogHeader>

        <Tabs defaultValue="colors">
          <TabsList>
            <TabsTrigger value="colors" data-testid="tab-colors">Colors</TabsTrigger>
            <TabsTrigger value="fonts" data-testid="tab-fonts">Font</TabsTrigger>
            <TabsTrigger value="shape" data-testid="tab-shape">Shape</TabsTrigger>
            <TabsTrigger value="saved" data-testid="tab-saved">Saved</TabsTrigger>
          </TabsList>

          <TabsContent value="colors" className="space-y-4">
            <div>
              <div className="text-xs uppercase tracking-widest font-mono-plex text-slate-500 mb-2">Presets</div>
              <div className="grid grid-cols-2 gap-2">
                {PRESETS.map((p) => {
                  const active = theme.id === p.id;
                  return (
                    <button key={p.id} onClick={() => setPreset(p.id)} data-testid={`theme-preset-${p.id}`}
                      className={`rounded-lg p-3 text-left border-2 transition-transform duration-200 hover:-translate-y-[2px] ${active ? "border-slate-900" : "border-slate-200"}`}>
                      <div className="h-10 rounded-md mb-2" style={{ background: grad(p) }} />
                      <div className="flex items-center justify-between">
                        <div className="text-sm font-medium">{p.label}</div>
                        {active && <Check className="w-4 h-4 text-slate-900" />}
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>
            <Card className="p-4 border-slate-200 shadow-none">
              <div className="text-xs uppercase tracking-widest font-mono-plex text-slate-500 mb-2">Custom mix</div>
              <div className="h-14 rounded-md mb-3" style={{ background: grad(theme) }} data-testid="theme-preview" />
              <div className="grid grid-cols-3 gap-3">
                {["from", "via", "to"].map((key) => (
                  <div key={key}>
                    <Label className="text-[10px] uppercase tracking-widest font-mono-plex">{key}</Label>
                    <div className="flex items-center gap-2 mt-1">
                      <Input type="color" value={theme[key]} onChange={(e) => setCustom({ [key]: e.target.value })} className="h-9 w-14 p-1" data-testid={`theme-color-${key}`} />
                      <Input value={theme[key]} onChange={(e) => setCustom({ [key]: e.target.value })} className="h-9 text-xs font-mono-plex" />
                    </div>
                  </div>
                ))}
              </div>
            </Card>
          </TabsContent>

          <TabsContent value="fonts">
            <div className="grid grid-cols-2 gap-2">
              {HEADING_FONTS.map((f) => {
                const active = theme.headingFontId === f.id;
                return (
                  <button key={f.id} onClick={() => setHeadingFont(f.id)} data-testid={`font-${f.id}`}
                    className={`rounded-lg p-4 text-left border-2 transition-transform duration-200 hover:-translate-y-[2px] ${active ? "border-slate-900" : "border-slate-200"}`}>
                    <div className="text-2xl font-black" style={{ fontFamily: f.stack }}>Aa</div>
                    <div className="flex items-center justify-between mt-2">
                      <div className="text-sm">{f.label}</div>
                      {active && <Check className="w-4 h-4" />}
                    </div>
                  </button>
                );
              })}
            </div>
          </TabsContent>

          <TabsContent value="shape">
            <div className="grid grid-cols-3 gap-2">
              {RADII.map((r) => {
                const active = theme.radiusId === r.id;
                return (
                  <button key={r.id} onClick={() => setRadius(r.id)} data-testid={`radius-${r.id}`}
                    className={`p-4 text-center border-2 transition-transform duration-200 hover:-translate-y-[2px] ${active ? "border-slate-900" : "border-slate-200"}`}
                    style={{ borderRadius: r.radius }}>
                    <div className="mx-auto w-14 h-10 bg-slate-900" style={{ borderRadius: r.radius }} />
                    <div className="text-sm mt-2">{r.label}</div>
                    {active && <Check className="w-4 h-4 mx-auto mt-1" />}
                  </button>
                );
              })}
            </div>
          </TabsContent>

          <TabsContent value="saved" className="space-y-3">
            <Card className="p-4 border-slate-200 shadow-none">
              <Label>Save current look as</Label>
              <div className="flex gap-2 mt-1">
                <Input placeholder="e.g. Morning · Client demo" value={saveName} onChange={(e) => setSaveName(e.target.value)} data-testid="save-theme-name" />
                <Button onClick={() => { if (!saveName.trim()) { toast.error("Name it first"); return; } saveCurrent(saveName.trim()); setSaveName(""); toast.success("Saved"); }} data-testid="save-theme-btn"><Save className="w-4 h-4 mr-1" /> Save</Button>
              </div>
            </Card>
            <div className="space-y-2">
              {saved.length === 0 && <div className="text-xs text-slate-400 italic py-4 text-center border border-dashed border-slate-200 rounded-md">No saved themes yet.</div>}
              {saved.map((s) => (
                <div key={s.name} className="flex items-center gap-3 border border-slate-200 rounded-md p-2" data-testid={`saved-${s.name}`}>
                  <div className="w-16 h-8 rounded" style={{ background: grad(s.theme) }} />
                  <div className="flex-1 text-sm">{s.name}</div>
                  <Button size="sm" variant="outline" onClick={() => applySaved(s.name)}>Apply</Button>
                  <Button size="icon" variant="ghost" onClick={() => removeSaved(s.name)}><Trash2 className="w-4 h-4 text-rose-500" /></Button>
                </div>
              ))}
            </div>
          </TabsContent>
        </Tabs>

        <DialogFooter className="flex-wrap gap-2">
          {user?.role === "admin" && (
            <Button variant="outline" onClick={async () => { await publishTeamTheme(); toast.success("Team theme published"); }} data-testid="publish-team-theme">
              <Upload className="w-4 h-4 mr-1" /> Publish to team
            </Button>
          )}
          <Button onClick={() => { setOpen(false); toast.success("Theme saved"); }} className="bg-slate-900 hover:bg-slate-800">Done</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
